// QRCodeView — the invite QR, rendered from the invite **link**.
//
// W40.6. This used to take a `svg` string from the backend and inject it with
// `dangerouslySetInnerHTML`, and the backend rendered it from the bare 6-digit
// code (`_render_qr_svg` → `qr.add_data(code)`). So the screen offered two ways
// to share an invite and they were not the same thing:
//
//   - the link below it, and the 分享 button, carried `{publicUrl}/invite/{code}`
//   - the QR handed the recipient six digits, to be typed in by hand
//
// while the copy above both read 「分享連結或 QR, 對方接受後會自動建立 Pair」.
//
// Two independent knowledge of the link's shape is what let those drift: the
// backend built the QR from the code, the frontend built the link from the code,
// and nothing compared them. Rendering here from the same `inviteUrl` string the
// rest of the screen uses makes the disagreement unrepresentable — there is one
// value, and the QR and the share sheet cannot hold different ones.
//
// Consequences worth stating, all of them improvements:
//   - no `dangerouslySetInnerHTML` (the old code carried a comment justifying it
//     as "server-issued, therefore safe", which was true and beside the point);
//   - no `mm` → px rewrite and no `SvgPathImage` workaround for the blank-box
//     bug those units caused in modern browsers — `marginSize` is a prop;
//   - the backend stops rendering a QR nobody looks at. See ADR-0099.
//
// `title` is not decoration. A screen-reader user cannot see that a square on a
// page leads somewhere, so the SVG carries the destination as its accessible
// name — which also makes the payload assertable from the DOM by a probe, so
// "the QR points at the link" is checkable at runtime and not only by reading
// this file.

import { QRCodeSVG } from "qrcode.react";

interface QRCodeViewProps {
  /**
   * Exactly what a scan should open — the same string shown and shared.
   *
   * Nullable on purpose. A QR with no destination is not a QR, and the caller's
   * value is derived state (`invite ? url : null`) that TypeScript cannot narrow
   * inside a branch it is not itself inside. Rather than cast that away — which
   * would hand `qrcode.react` a `null` and get a thrown error in a render path
   * nobody tested — the component renders nothing and says so.
   */
  value: string | null;
  /** Accessible name; defaults to something that names the destination. */
  title?: string;
  /** Rendered edge length in px. The container is what the spec sizes. */
  size?: number;
}

export function QRCodeView({ value, title, size = 200 }: QRCodeViewProps) {
  if (!value) return null;

  return (
    <div
      data-testid="qr-code"
      // The payload, in the DOM. Not a test hook for its own sake: it is the
      // same string the accessible name carries, and it is what makes
      // "the QR and the link agree" verifiable from outside the component.
      data-value={value}
      className="bg-paper-deep border border-hairline rounded-md p-4 inline-block"
    >
      <QRCodeSVG
        value={value}
        size={size}
        // `L` is what the backend used; `M` survives a smudged phone screen at
        // no cost in density worth the difference here.
        level="M"
        marginSize={0}
        bgColor="#ffffff"
        fgColor="#000000"
        title={title ?? `邀請 QR 碼，掃描後開啟 ${value}`}
      />
    </div>
  );
}
